import type { WorldFlowHop, WorldFlowLink } from '@/analysis/worldFlowTypes'
import type { ClusterArc } from './PipelineArcs'
import { hopKey } from './hopPlacement'
import { worstTier } from './segments'

/**
 * Collapse studio→client (city-grain keys that are continent names at continent
 * grain) into one aggregated studio→continent arc per pair.
 *
 * Uses only studio→city links (partitioned load). Does NOT fan edge→client
 * totals through shared CDN edges — that double-counted users/streams/severity.
 */
export function buildContinentClusterArcs(
  links: WorldFlowLink[],
  hops: WorldFlowHop[],
): ClusterArc[] {
  const hopByKey = new Map(hops.map((h) => [hopKey(h.type, h.value), h]))
  const out = new Map<string, ClusterArc>()

  for (const l of links) {
    if (l.from.type !== 'studio' || l.to.type !== 'city') continue

    const studio = l.from.value
    const continent = l.to.value
    const id = `studio:${studio}->continent:${continent}`
    const prev = out.get(id)

    if (!prev) {
      const studioHop = hopByKey.get(hopKey('studio', studio))
      const contHop = hopByKey.get(hopKey('city', continent))
      out.set(id, {
        id,
        fromCluster: hopKey('studio', studio),
        toCluster: hopKey('city', continent),
        usersTotal: l.usersTotal,
        totalStreams: l.totalStreams,
        avgSeverity: l.avgSeverity,
        worstMetric: l.worstMetric,
        metrics: l.metrics,
        label: `${studioHop?.label ?? studio} → ${contHop?.label ?? continent}`,
      })
    } else {
      prev.usersTotal += l.usersTotal
      prev.totalStreams += l.totalStreams
      prev.avgSeverity = worstTier(prev.avgSeverity, l.avgSeverity)
      if (!prev.worstMetric && l.worstMetric) prev.worstMetric = l.worstMetric
    }
  }

  return [...out.values()]
}
